// ═══════════════════════════════════════════════════
//  Fabric roll packing — types and algorithm.
//  See docs/superpowers/specs/2026-04-14-fabric-roll-packing-design.md
// ═══════════════════════════════════════════════════

export type FabricPatternDirection = 'none' | 'railroad' | 'up_the_bolt';

/** A single fabric panel that needs to be cut from a roll. */
export interface Piece {
  slot_id: string;
  slot_name: string;
  panel_type: 'front' | 'side' | 'back';
  section_index: number;
  /** 0 or 1 for sides; undefined for front. */
  panel_index?: number;
  /** Panel-frame width (one of the two flat dimensions). */
  width_in: number;
  /** Panel-frame length (the other flat dimension). */
  length_in: number;
  /** Which of the panel's two axes is the cushion vertical when installed. */
  design_up_axis: 'width' | 'length';
}

export interface RollSpec {
  width_in: number;
  /** 30 yards = 1080 in for our use. */
  max_length_in: number;
}

export interface PackedPiece extends Piece {
  roll_index: number;
  x_in: number;
  y_in: number;
  width_on_roll: number;
  length_on_roll: number;
  /** True when the alternate orientation was selected (only possible for pattern_direction = 'none'). */
  rotated: boolean;
  /** Set when this packed entry is one panel of a seamed cut. */
  seam_index?: number;
  seam_total?: number;
}

export interface PackingResult {
  rolls: Array<{
    roll_index: number;
    length_consumed_in: number;
    pieces: PackedPiece[];
  }>;
  total_length_in: number;
  total_rolls: number;
  /** Pieces too large to fit on any roll (length exceeds max_length_in in both orientations). */
  unplaceable_pieces: Piece[];
  warnings: string[];
}

/**
 * Cost-enriched packing result for a single fabric material across all slots
 * that use it. Emitted as a top-level `fabric_packings[]` entry on product
 * cost results so UIs can render a per-material audit view.
 */
export interface FabricPackingResult {
  material_id: string;
  material_name: string;
  roll_width_in: number;
  pattern_direction: FabricPatternDirection;
  rolls: PackingResult['rolls'];
  total_rolls: number;
  /** Summed consumed roll-length across all rolls (inches, pre-multipliers). */
  total_length_in: number;
  /** total_length_in * (1 + effective_design_pct + pattern_allowance_pct + material_waste_factor). */
  final_length_in: number;
  /** final_length_in / 36. */
  final_yards: number;
  /** Slot IDs whose pieces contributed to this packing. */
  contributing_slot_ids: string[];
  /** Resolved (raw) design pct from the resolution chain. */
  design_pct: number;
  /** design_pct × computeDesignBearingFraction(pieces). What was actually applied to the multiplier. */
  effective_design_pct: number;
  pattern_allowance_pct: number;
  material_waste_factor: number;
  cost: number;
  currency_note?: string;
  warnings: string[];
}

// ── Skyline-BLF helpers ──

interface SkylineSegment {
  x_start: number;
  x_end: number; // exclusive
  y_top: number;
}

interface WorkingRoll {
  roll_index: number;
  length_consumed_in: number;
  pieces: PackedPiece[];
  skyline: SkylineSegment[];
}

function newRoll(roll_index: number, width_in: number): WorkingRoll {
  return {
    roll_index,
    length_consumed_in: 0,
    pieces: [],
    skyline: [{ x_start: 0, x_end: width_in, y_top: 0 }],
  };
}

/** Find the lowest-y position where a piece of width `w` fits; tiebreak by lowest x. */
function findPlacement(
  skyline: SkylineSegment[],
  w: number,
  rollWidth: number,
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  for (let i = 0; i < skyline.length; i++) {
    const startX = skyline[i].x_start;
    if (startX + w > rollWidth) break;
    let y = skyline[i].y_top;
    let j = i;
    while (j < skyline.length && skyline[j].x_start < startX + w) {
      y = Math.max(y, skyline[j].y_top);
      j++;
    }
    if (best === null || y < best.y || (y === best.y && startX < best.x)) {
      best = { x: startX, y };
    }
  }
  return best;
}

function updateSkyline(
  skyline: SkylineSegment[],
  x: number,
  w: number,
  newTop: number,
): SkylineSegment[] {
  const xEnd = x + w;
  const out: SkylineSegment[] = [];
  for (const seg of skyline) {
    if (seg.x_end <= x || seg.x_start >= xEnd) {
      out.push(seg);
    } else {
      if (seg.x_start < x) out.push({ x_start: seg.x_start, x_end: x, y_top: seg.y_top });
      if (seg.x_end > xEnd) out.push({ x_start: xEnd, x_end: seg.x_end, y_top: seg.y_top });
    }
  }
  out.push({ x_start: x, x_end: xEnd, y_top: newTop });
  out.sort((a, b) => a.x_start - b.x_start);
  // Merge adjacent segments with same y_top
  const merged: SkylineSegment[] = [];
  for (const s of out) {
    const last = merged[merged.length - 1];
    if (last && last.x_end === s.x_start && last.y_top === s.y_top) {
      last.x_end = s.x_end;
    } else {
      merged.push({ ...s });
    }
  }
  return merged;
}

/**
 * Expand a piece into one or more placement-ready sub-pieces, picking the best
 * orientation (for `none`) and splitting into seam panels if the chosen
 * orientation exceeds the roll width.
 */
function expandWithSeams(
  p: Piece,
  roll: RollSpec,
  patternDirection: FabricPatternDirection,
): Array<{
  source: Piece;
  width_on_roll: number;
  length_on_roll: number;
  rotated: boolean;
  seam_index?: number;
  seam_total?: number;
}> {
  const designUp = p.design_up_axis === 'width' ? p.width_in : p.length_in;
  const other = p.design_up_axis === 'width' ? p.length_in : p.width_in;

  const candidates: Array<{ w: number; l: number; rotated: boolean }> = [];
  if (patternDirection === 'up_the_bolt') {
    candidates.push({ w: other, l: designUp, rotated: false });
  } else if (patternDirection === 'railroad') {
    candidates.push({ w: designUp, l: other, rotated: false });
  } else {
    candidates.push({ w: p.width_in, l: p.length_in, rotated: false });
    candidates.push({ w: p.length_in, l: p.width_in, rotated: true });
  }

  // Prefer a candidate that fits the roll width without seaming.
  const fitsNoSeam = candidates.filter((c) => c.w <= roll.width_in);
  let chosen: { w: number; l: number; rotated: boolean };
  if (fitsNoSeam.length > 0) {
    fitsNoSeam.sort((a, b) => a.l - b.l);
    chosen = fitsNoSeam[0];
  } else {
    candidates.sort((a, b) => a.w - b.w);
    chosen = candidates[0];
  }

  if (chosen.w <= roll.width_in) {
    return [{ source: p, width_on_roll: chosen.w, length_on_roll: chosen.l, rotated: chosen.rotated }];
  }
  // Seam: split into N panels each occupying the full roll width.
  const n = Math.ceil(chosen.w / roll.width_in);
  return Array.from({ length: n }, (_, i) => ({
    source: p,
    width_on_roll: roll.width_in,
    length_on_roll: chosen.l,
    rotated: chosen.rotated,
    seam_index: i,
    seam_total: n,
  }));
}

/**
 * Pack a collection of fabric pieces onto a roll using Skyline Bottom-Left-Fill.
 * Implementation lands incrementally across the next few tasks.
 */
export function packFabricPieces(
  pieces: Piece[],
  roll: RollSpec,
  patternDirection: FabricPatternDirection,
): PackingResult {
  const upfrontWarnings: string[] = [];
  const effectiveRoll: RollSpec = { ...roll };
  if (!Number.isFinite(roll.width_in) || roll.width_in <= 0) {
    upfrontWarnings.push(`Invalid roll width (${roll.width_in}); defaulted to 54"`);
    effectiveRoll.width_in = 54;
  }
  if (!Number.isFinite(roll.max_length_in) || roll.max_length_in <= 0) {
    effectiveRoll.max_length_in = 1080;
  }

  if (pieces.length === 0) {
    return {
      rolls: [],
      total_length_in: 0,
      total_rolls: 0,
      unplaceable_pieces: [],
      warnings: upfrontWarnings,
    };
  }

  // Reject zero/negative-dim pieces as unplaceable up front.
  const validPieces: Piece[] = [];
  const invalidPieces: Piece[] = [];
  for (const p of pieces) {
    if (
      !Number.isFinite(p.width_in) ||
      !Number.isFinite(p.length_in) ||
      p.width_in <= 0 ||
      p.length_in <= 0
    ) {
      invalidPieces.push(p);
    } else {
      validPieces.push(p);
    }
  }

  // Sort source pieces by max dimension descending (place big pieces first).
  const sorted = [...validPieces].sort((a, b) => {
    const al = Math.max(a.width_in, a.length_in);
    const bl = Math.max(b.width_in, b.length_in);
    return bl - al;
  });

  // Expand into placement-ready sub-pieces (handles seaming + orientation choice).
  const expanded = sorted.flatMap((p) => expandWithSeams(p, effectiveRoll, patternDirection));
  // Re-sort by length_on_roll desc so the largest placements come first.
  expanded.sort((a, b) => b.length_on_roll - a.length_on_roll);

  const workingRolls: WorkingRoll[] = [newRoll(0, effectiveRoll.width_in)];
  const unplaceable: Piece[] = [...invalidPieces];
  const seenUnplaceable = new Set<Piece>(invalidPieces);

  for (const e of expanded) {
    if (e.length_on_roll > effectiveRoll.max_length_in) {
      if (!seenUnplaceable.has(e.source)) {
        seenUnplaceable.add(e.source);
        unplaceable.push(e.source);
      }
      continue;
    }

    let placed: PackedPiece | null = null;
    for (const r of workingRolls) {
      const spot = findPlacement(r.skyline, e.width_on_roll, effectiveRoll.width_in);
      if (spot && spot.y + e.length_on_roll <= effectiveRoll.max_length_in) {
        placed = {
          ...e.source,
          roll_index: r.roll_index,
          x_in: spot.x,
          y_in: spot.y,
          width_on_roll: e.width_on_roll,
          length_on_roll: e.length_on_roll,
          rotated: e.rotated,
          seam_index: e.seam_index,
          seam_total: e.seam_total,
        };
        r.pieces.push(placed);
        r.skyline = updateSkyline(r.skyline, spot.x, e.width_on_roll, spot.y + e.length_on_roll);
        r.length_consumed_in = Math.max(r.length_consumed_in, spot.y + e.length_on_roll);
        break;
      }
    }
    if (!placed) {
      // Open a new roll.
      const next = newRoll(workingRolls.length, effectiveRoll.width_in);
      const spot = findPlacement(next.skyline, e.width_on_roll, effectiveRoll.width_in);
      if (!spot || spot.y + e.length_on_roll > effectiveRoll.max_length_in) {
        if (!seenUnplaceable.has(e.source)) {
          seenUnplaceable.add(e.source);
          unplaceable.push(e.source);
        }
        continue;
      }
      placed = {
        ...e.source,
        roll_index: next.roll_index,
        x_in: spot.x,
        y_in: spot.y,
        width_on_roll: e.width_on_roll,
        length_on_roll: e.length_on_roll,
        rotated: e.rotated,
        seam_index: e.seam_index,
        seam_total: e.seam_total,
      };
      next.pieces.push(placed);
      next.skyline = updateSkyline(next.skyline, spot.x, e.width_on_roll, spot.y + e.length_on_roll);
      next.length_consumed_in = Math.max(next.length_consumed_in, spot.y + e.length_on_roll);
      workingRolls.push(next);
    }
  }

  // Drop any rolls that received no pieces (e.g., when all pieces were unplaceable).
  const rolls = workingRolls
    .filter((r) => r.pieces.length > 0)
    .map(({ skyline: _s, ...rest }) => rest);

  const warnings: string[] = [...upfrontWarnings];
  for (const u of unplaceable) {
    warnings.push(
      `Piece exceeds roll capacity (slot=${u.slot_name}, panel=${u.panel_type}, ${u.width_in}x${u.length_in} in)`,
    );
  }

  return {
    rolls,
    total_length_in: rolls.reduce((s, r) => s + r.length_consumed_in, 0),
    total_rolls: rolls.length,
    unplaceable_pieces: unplaceable,
    warnings,
  };
}
